import { useState, useCallback, useEffect } from 'react';
import { message } from 'antd';
import employeeApi, { F901 } from '../../../services/employeeApi';

type Mode = 'browse' | 'add' | 'edit';

export function useEmployeeForm() {
  const [mode, setMode] = useState<Mode>('browse');
  const [records, setRecords] = useState<F901[]>([]);
  const [currentIdx, setCurrentIdx] = useState(0);
  const [loading, setLoading] = useState(false);

  // Key fields
  const [f901_01, setF901_01] = useState('');
  const [f901_11, setF901_11] = useState('');
  const [f901_12, setF901_12] = useState('');
  const [f901_13, setF901_13] = useState<string | null>(null);
  const [f901_14, setF901_14] = useState(0);
  const [f901_19, setF901_19] = useState('');
  const [f901_20, setF901_20] = useState('');
  const [f901_25, setF901_25] = useState('');
  const [f901_26, setF901_26] = useState('');
  const [f901_27, setF901_27] = useState('');
  const [f901_28, setF901_28] = useState('');
  const [f901_30, setF901_30] = useState('');
  const [f901_31, setF901_31] = useState<string | null>(null);
  const [f901_32, setF901_32] = useState('');
  const [f901_33, setF901_33] = useState('');
  const [f901_34, setF901_34] = useState<string | null>(null);
  const [f901_35, setF901_35] = useState<string | null>(null);
  const [f901_37, setF901_37] = useState('');
  const [f901_52, setF901_52] = useState('');
  const [f901_55, setF901_55] = useState('');
  const [f901_60, setF901_60] = useState(false);
  const [f901_91, setF901_91] = useState('');
  const [f901_92, setF901_92] = useState<string | null>(null);
  const [f901_93, setF901_93] = useState('');

  // Employment status display (BR-07)
  const [statusLabel, setStatusLabel] = useState('');

  const loadRecords = useCallback(async () => {
    setLoading(true);
    try {
      const res = await employeeApi.list(1, 500);
      setRecords(res.data || []);
      if ((res.data || []).length > 0) {
        setCurrentIdx(0);
      }
    } catch {
      message.error('載入資料失敗');
    } finally {
      setLoading(false);
    }
  }, []);

  const computeStatus = useCallback((rec: F901) => {
    if (rec.f901_34) {
      setStatusLabel('已離職');
    } else if (!rec.f901_35) {
      setStatusLabel('試用人員');
    } else {
      setStatusLabel('正式人員');
    }
  }, []);

  const syncFields = useCallback((rec: F901) => {
    setF901_01(rec.f901_01 || '');
    setF901_11(rec.f901_11 || '');
    setF901_12(rec.f901_12 || '');
    setF901_13(rec.f901_13);
    setF901_14(rec.f901_14 || 0);
    setF901_19(rec.f901_19 || '');
    setF901_20(rec.f901_20 || '');
    setF901_25(rec.f901_25 || '');
    setF901_26(rec.f901_26 || '');
    setF901_27(rec.f901_27 || '');
    setF901_28(rec.f901_28 || '');
    setF901_30(rec.f901_30 || '');
    setF901_31(rec.f901_31);
    setF901_32(rec.f901_32 || '');
    setF901_33(rec.f901_33 || '');
    setF901_34(rec.f901_34);
    setF901_35(rec.f901_35);
    setF901_37(rec.f901_37 || '');
    setF901_52(rec.f901_52 || '');
    setF901_55(rec.f901_55 || '');
    setF901_60(rec.f901_60 || false);
    setF901_91(rec.f901_91 || '');
    setF901_92(rec.f901_92);
    setF901_93(rec.f901_93 || '');
    computeStatus(rec);
  }, [computeStatus]);

  useEffect(() => {
    loadRecords();
  }, [loadRecords]);

  useEffect(() => {
    if (records.length > 0 && currentIdx >= 0 && currentIdx < records.length) {
      syncFields(records[currentIdx]);
    }
  }, [records, currentIdx, syncFields]);

  // Navigation
  const navFirst = useCallback(() => setCurrentIdx(0), []);
  const navPrev = useCallback(() => setCurrentIdx((i) => Math.max(0, i - 1)), []);
  const navNext = useCallback(() => setCurrentIdx((i) => Math.min(records.length - 1, i + 1)), [records.length]);
  const navLast = useCallback(() => setCurrentIdx(records.length - 1), [records.length]);

  const startAdd = useCallback(() => {
    setMode('add');
    setF901_01(''); setF901_11(''); setF901_12('');
    setF901_13(null); setF901_14(0); setF901_19('');
    setF901_20(''); setF901_25(''); setF901_26('');
    setF901_27(''); setF901_28(''); setF901_30('');
    setF901_31(null); setF901_32(''); setF901_33('');
    setF901_34(null); setF901_35(null); setF901_37('');
    setF901_52(''); setF901_55(''); setF901_60(false);
    setF901_91(''); setF901_92(null); setF901_93('');
    setStatusLabel('');
  }, []);

  const startEdit = useCallback(() => {
    setMode('edit');
  }, []);

  const cancel = useCallback(() => {
    setMode('browse');
    if (records.length > 0) {
      syncFields(records[currentIdx]);
    }
  }, [records, currentIdx, syncFields]);

  const buildPayload = useCallback((): Partial<F901> => ({
    f901_01: f901_01.trim(),
    f901_11: f901_11.trim(),
    f901_12: f901_12,
    f901_13: f901_13,
    f901_14: f901_14,
    f901_19: f901_19,
    f901_20: f901_20,
    f901_25: f901_25,
    f901_26: f901_26,
    f901_27: f901_27,
    f901_28: f901_28,
    f901_30: f901_30,
    f901_31: f901_31,
    f901_32: f901_32,
    f901_33: f901_33,
    f901_34: f901_34,
    f901_35: f901_35,
    f901_37: f901_37,
    f901_52: f901_52,
    f901_55: f901_55,
    f901_60: f901_60,
  }), [f901_01, f901_11, f901_12, f901_13, f901_14, f901_19, f901_20,
       f901_25, f901_26, f901_27, f901_28, f901_30, f901_31,
       f901_32, f901_33, f901_34, f901_35, f901_37,
       f901_52, f901_55, f901_60]);

  const save = useCallback(async () => {
    setLoading(true);
    try {
      const payload = buildPayload();
      if (mode === 'add') {
        await employeeApi.create(payload);
        message.success('新增成功');
      } else {
        await employeeApi.update(f901_01, payload);
        message.success('儲存成功');
      }
      setMode('browse');
      await loadRecords();
    } catch (err: any) {
      if (err?.response?.status === 422) {
        const errors = err.response.data?.errors;
        if (Array.isArray(errors) && errors.length > 0) {
          message.error(errors[0].message);
        }
      } else {
        message.error('儲存失敗');
      }
    } finally {
      setLoading(false);
    }
  }, [mode, f901_01, buildPayload, loadRecords]);

  const doDelete = useCallback(async () => {
    if (!f901_01) return;
    setLoading(true);
    try {
      await employeeApi.delete(f901_01);
      message.success('刪除成功');
      await loadRecords();
    } catch {
      message.error('刪除失敗');
    } finally {
      setLoading(false);
    }
  }, [f901_01, loadRecords]);

  const quickSearch = useCallback((keyword: string) => {
    const idx = records.findIndex((r) =>
      r.f901_01.toUpperCase().startsWith(keyword.toUpperCase()) ||
      r.f901_11.includes(keyword)
    );
    if (idx >= 0) {
      setCurrentIdx(idx);
    } else {
      message.warning('查無資料');
    }
  }, [records]);

  return {
    mode, loading, records, currentIdx, statusLabel,
    f901_01, f901_11, f901_12, f901_13, f901_14,
    f901_19, f901_20, f901_25, f901_26, f901_27, f901_28,
    f901_30, f901_31, f901_32, f901_33, f901_34, f901_35, f901_37,
    f901_52, f901_55, f901_60,
    f901_91, f901_92, f901_93,
    setF901_01, setF901_11, setF901_12, setF901_13, setF901_14,
    setF901_19, setF901_20, setF901_25, setF901_26, setF901_27, setF901_28,
    setF901_30, setF901_31, setF901_32, setF901_33, setF901_34, setF901_35, setF901_37,
    setF901_52, setF901_55, setF901_60,
    navFirst, navPrev, navNext, navLast,
    startAdd, startEdit, cancel, save, doDelete,
    quickSearch,
    recordCount: records.length,
    recordPosition: records.length > 0 ? currentIdx + 1 : 0,
  };
}
